// frontend/dashboard/src/router/modules/companyRoutes.js
// Responsible for: company management routes — staff, settings
// English: Protected routes for company-level management.
//          Tenant is derived from JWT (req.auth.tenant_id), never from URL input.
// Thai: routes สำหรับจัดการระดับบริษัท ต้องการ authentication ทั้งหมด
//       tenant ได้จาก JWT ไม่ใช่จาก URL input

const ROUTE_DEBUG_NAMESPACE = '[router/companyRoutes]';

const PAGE_REGISTRY = {
  CompanyStaffPage: () => import('../../pages/CompanyStaffPage.vue'),
  ProfilePage:      () => import('../../pages/ProfilePage.vue'),
  SiteListPage:     () => import('../../pages/SiteListPage.vue'),
  SiteSetupPage:    () => import('../../pages/SiteSetupPage.vue'),
};

function buildLazyPage(registryKey, debugLabel) {
  return async () => {
    const loader = PAGE_REGISTRY[registryKey];
    if (!loader) throw new Error(`${ROUTE_DEBUG_NAMESPACE} No registry entry for: ${registryKey}`);

    try {
      const module = await loader();
      // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
      // debugLog(`${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_SUCCESS]`, { registryKey, debugLabel });
      return module;
    } catch (error) {
      console.error(`${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_ERROR]`, {
        registryKey,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  };
}

const companyRoutes = [
  {
    path: '/profile',
    name: 'Profile',
    component: buildLazyPage('ProfilePage', 'ProfilePage'),
    meta: {
      section:      'company',
      requiresAuth: true,
      layout:       'dashboard',
      title:        'My Profile',
    },
  },
  {
    path: '/company/staff',
    name: 'CompanyStaff',
    component: buildLazyPage('CompanyStaffPage', 'CompanyStaffPage'),
    meta: {
      section:      'company',
      requiresAuth: true,
      layout:       'dashboard',
      title:        'Team Members',
    },
  },
  {
    path: '/company/sites',
    name: 'SiteList',
    component: buildLazyPage('SiteListPage', 'SiteListPage'),
    meta: {
      section:      'company',
      requiresAuth: true,
      layout:       'dashboard',
      title:        'Sites',
    },
  },
  // English: Static create route must come before the dynamic edit route so Vue Router
  // never interprets the literal segment `new` as a site_id.
  // ไทย: route สร้างแบบ static ต้องอยู่ก่อน route dynamic สำหรับ edit เพื่อไม่ให้ Vue Router
  // ตีความคำว่า `new` เป็น site_id
  {
    path: '/company/sites/new',
    name: 'SiteSetup',
    component: buildLazyPage('SiteSetupPage', 'SiteSetupPage'),
    meta: {
      section:      'company',
      requiresAuth: true,
      layout:       'dashboard',
      title:        'Create Site',
    },
  },
  // English: Edit route reuses SiteSetupPage in edit mode, driven by `route.params.site_id`.
  // ไทย: route แก้ไข reuse SiteSetupPage ในโหมด edit โดยอิง `route.params.site_id`
  {
    path: '/company/sites/:site_id/edit',
    name: 'SiteEdit',
    component: buildLazyPage('SiteSetupPage', 'SiteSetupPage'),
    meta: {
      section:      'company',
      requiresAuth: true,
      layout:       'dashboard',
      title:        'Edit Site',
    },
  },
];

// [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
// debugLog(`${ROUTE_DEBUG_NAMESPACE} [ROUTES_REGISTERED]`, {
//   routeCount: companyRoutes.length,
//   routeNames: companyRoutes.map((r) => r.name),
// });

export default companyRoutes;
